<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import AppInput from '@/components/common/AppInput.vue'
import AppTextarea from '@/components/common/AppTextarea.vue'
import type { NotificationData } from '@/modules/notifications/api'
import { notificationSchema } from '@/core/validation/schemas'
import { IconInfoCircle } from '@tabler/icons-vue'

const { t } = useI18n()

interface Props {
  loading?: boolean
  error?: string
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'submit', data: NotificationData): void
  (e: 'cancel'): void
}>()

const form = ref({
  recipient: '',
  subject: '',
  message: ''
})

// Validation rules - používá centrální schema
const rules = computed(() => notificationSchema())

// Initialize validation
const v$ = useVuelidate(rules, form)

const submit = async () => {
  // Validate before submitting
  const isFormValid = await v$.value.$validate()
  
  if (!isFormValid) {
    return
  }

  const notificationData: NotificationData = {
    recipient: form.value.recipient,
    subject: form.value.subject,
    message: form.value.message,
  }

  emit('submit', notificationData)
}

const submitForm = () => {
  submit()
}

defineExpose({
  submitForm
})
</script>

<template>
  <div>
    <form class="space-y-6" @submit.prevent="submitForm">
      
      <div
        v-if="error"
        class="bg-red-500/10 border border-red-500/20 p-4 rounded-lg text-red-400"
      >
        {{ error }}
      </div>

      <div class="bg-base-200 border border-white/[0.06] rounded-lg p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <IconInfoCircle class="h-5 w-5 text-amber-400" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-gray-300">
              {{ t('notifications.form.info.title') }}
            </h3>
            <div class="mt-2 text-sm text-gray-400">
              <p>{{ t('notifications.form.info.description') }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="space-y-6">
        <AppInput
          v-model="form.recipient"
          type="email"
          :label="t('notifications.form.fields.recipientEmail')"
          :placeholder="t('notifications.form.placeholders.recipientEmail')"
          required
          :error="v$.recipient.$error ? String(v$.recipient.$errors[0].$message) : ''"
        />

        <AppInput
          v-model="form.subject"
          :label="t('notifications.form.fields.subject')"
          :placeholder="t('notifications.form.placeholders.subject')"
          required
          :error="v$.subject.$error ? String(v$.subject.$errors[0].$message) : ''"
        />

        <AppTextarea
          v-model="form.message"
          :label="t('notifications.form.fields.message')"
          :placeholder="t('notifications.form.placeholders.message')"
          :rows="8"
          required
          :error="v$.message.$error ? String(v$.message.$errors[0].$message) : ''"
        />
      </div>
    </form>
  </div>
</template>
